/**
 * CustomerWaiverForm — AISC §1.7.0 Phase 4 customer waiver create / edit /
 * lifecycle.
 *
 * Redesigned 2026-05-05 (CR-2026-FE-WAIVER-FORM-REDESIGN, Class P) onto the
 * PaperForm component library to meet the SFM "True-to-Form, Apple-like"
 * Quality Standard. A3-derived (no Chesakl source); high-impact for ABC's
 * customer-side approval flow.
 *
 * URL: /customer-waivers/new or /customer-waivers/:id
 */

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Stack, Typography, Button, Alert, Box, Grid, TextField, MenuItem,
  Breadcrumbs, Link as MuiLink, IconButton, Divider, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, ReplyRounded,
  CheckCircleOutlineRounded, CancelRounded, GavelOutlined,
} from "@mui/icons-material";

import {
  getWaiver, createWaiver, updateWaiver, receiveWaiver, activateWaiver, revokeWaiver,
  WAIVER_SCOPES, allowedWaiverTransitions,
} from "../../utils/customerWaiversApi";
import {
  PaperFormCard, PaperFormHeader, PaperFormSection, PaperFormFooter,
  FieldLabel,
} from "../../components/PaperForm";

const DEFAULT_REQUEST_TEXT = `Per AISC §1.7.0 §3.2, please confirm in writing your election to waive the inspection body's standard pre-fabrication drawing approval requirement on this project.`;

export default function CustomerWaiverForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const isCreate = !id || id === "new";
  const jobIdParam = params.get("job_id") || "";

  const [waiver, setWaiver] = useState(null);
  const [jobId, setJobId] = useState(jobIdParam);
  const [scope, setScope] = useState("DRAWING_APPROVAL");
  const [requestText, setRequestText] = useState(DEFAULT_REQUEST_TEXT);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveUntil, setEffectiveUntil] = useState("");

  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState(null);

  // Dialogs
  const [openReceive, setOpenReceive] = useState(false);
  const [openRevoke, setOpenRevoke] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [responseDocId, setResponseDocId] = useState("");
  const [revokeReason, setRevokeReason] = useState("");

  const refresh = () => {
    if (isCreate) return;
    setLoading(true);
    getWaiver(id)
      .then((w) => {
        setWaiver(w);
        setJobId(String(w.job_id || ""));
        setScope(w.scope || "DRAWING_APPROVAL");
        setRequestText(w.customer_request_text || "");
        setEffectiveFrom(w.effective_from || "");
        setEffectiveUntil(w.effective_until || "");
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [id, isCreate]);

  const handleSave = async () => {
    setError(null);
    if (!jobId) {
      setError("Job No. is required.");
      return;
    }
    if (!requestText.trim()) {
      setError("Customer Request Text is required — this is the formal ask A3 sends to the customer.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        job_id: Number(jobId), scope,
        customer_request_text: requestText,
        effective_from: effectiveFrom || null,
        effective_until: effectiveUntil || null,
      };
      if (isCreate) {
        const w = await createWaiver(payload);
        navigate(`/customer-waivers/${w.id}`);
      } else {
        const { job_id: _ignore, ...editable } = payload;
        await updateWaiver(id, editable);
        refresh();
      }
    } catch (err) {
      setSaving(false);
      setError(err?.response?.data?.error || err?.message);
    }
  };

  const handleReceive = async () => {
    if (!responseText && !responseDocId) {
      setError("Either response text OR a signed document ID is required.");
      return;
    }
    setActing(true);
    try {
      await receiveWaiver(id, {
        customer_response_text: responseText || null,
        customer_response_doc_id: responseDocId ? Number(responseDocId) : null,
      });
      setOpenReceive(false); setResponseText(""); setResponseDocId(""); refresh();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message);
    } finally { setActing(false); }
  };

  const handleActivate = async () => {
    setActing(true);
    try { await activateWaiver(id); refresh(); }
    catch (err) { setError(err?.response?.data?.error || err?.message); }
    finally { setActing(false); }
  };

  const handleRevoke = async () => {
    if (!revokeReason || revokeReason.trim().length < 4) {
      setError("Revoke reason is required (min 4 characters).");
      return;
    }
    setActing(true);
    try { await revokeWaiver(id, revokeReason); setOpenRevoke(false); setRevokeReason(""); refresh(); }
    catch (err) { setError(err?.response?.data?.error || err?.message); }
    finally { setActing(false); }
  };

  const status = waiver?.status || "REQUESTED";
  const next = isCreate ? [] : allowedWaiverTransitions(status);
  const canEdit = isCreate || status === "REQUESTED";

  const waiverNumber = waiver?.id ? `#${waiver.id}` : (isCreate ? "Assigned automatically" : `#${id}`);
  const requestDate = useMemo(() => {
    if (waiver?.created_at) return new Date(waiver.created_at).toLocaleDateString();
    return new Date().toLocaleDateString();
  }, [waiver]);

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Loading waiver…</Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isCreate ? "New Customer Waiver" : `Customer Waiver #${id}`} | SFM</title>
      </Helmet>

      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: "md", mx: "auto", pt: 3, px: { xs: 2, md: 3 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Breadcrumbs>
              <MuiLink href="/" color="inherit" underline="hover">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <HomeRounded fontSize="small" />
                  <span>Home</span>
                </Stack>
              </MuiLink>
              <MuiLink href="/customer-waivers" color="inherit" underline="hover">
                Customer Waivers
              </MuiLink>
              <Typography color="text.primary">{isCreate ? "New" : `#${id}`}</Typography>
            </Breadcrumbs>
            <IconButton onClick={() => navigate("/customer-waivers")} aria-label="Back to Waiver list">
              <ArrowBackRounded />
            </IconButton>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!isCreate && status === "ACTIVE" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Active waiver — <code>jobs.customer_drawing_approval_waived = 1</code> mirrored on Job #{waiver?.job_id}.
            </Alert>
          )}
        </Box>

        <PaperFormCard>
          <PaperFormHeader
            formId="SFM · Customer Waiver"
            title="Customer Waiver Request"
            subtitle="AISC §1.7.0 §3.2 customer election to waive standard drawing approval"
            status={status}
            number={waiverNumber}
            numberLabel="Waiver No."
            date={requestDate}
            icon="article"
          />

          {/* TWO-COLUMN METADATA GRID */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <PaperFormSection label="Project & Scope" bottomPadding={0}>
                <Box>
                  <FieldLabel required>Job No.</FieldLabel>
                  <TextField
                    fullWidth size="small" type="number"
                    placeholder="e.g., 24-101"
                    value={jobId}
                    onChange={(e) => setJobId(e.target.value)}
                    disabled={!isCreate}
                    helperText={isCreate ? null : "Job is locked once a waiver is created."}
                  />
                </Box>
                <Box>
                  <FieldLabel required hint="what is being waived">Scope</FieldLabel>
                  <TextField
                    select fullWidth size="small"
                    value={scope}
                    onChange={(e) => setScope(e.target.value)}
                    disabled={!canEdit}
                  >
                    {WAIVER_SCOPES.map((s) => (
                      <MenuItem key={s} value={s}>{s.replace(/_/g, " ")}</MenuItem>
                    ))}
                  </TextField>
                </Box>
              </PaperFormSection>
            </Grid>

            <Grid item xs={12} md={6}>
              <PaperFormSection label="Effective Window" hint="optional · scope-bounded validity">
                <Box>
                  <FieldLabel>Effective From</FieldLabel>
                  <TextField
                    fullWidth size="small" type="date"
                    InputLabelProps={{ shrink: true }}
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                    disabled={!canEdit}
                  />
                </Box>
                <Box>
                  <FieldLabel>Effective Until</FieldLabel>
                  <TextField
                    fullWidth size="small" type="date"
                    InputLabelProps={{ shrink: true }}
                    value={effectiveUntil}
                    onChange={(e) => setEffectiveUntil(e.target.value)}
                    disabled={!canEdit}
                  />
                </Box>
              </PaperFormSection>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 4 }} />

          {/* REQUEST TEXT */}
          <PaperFormSection
            label="Customer Request Text"
            hint="A3's formal written request · sent to the customer for sign-off"
          >
            <TextField
              fullWidth multiline rows={6}
              value={requestText}
              onChange={(e) => setRequestText(e.target.value)}
              disabled={!canEdit}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
              The customer's signed response is captured separately via the
              "Record response" action below once status is REQUESTED → RECEIVED.
            </Typography>
          </PaperFormSection>

          {/* CUSTOMER RESPONSE (post-RECEIVED, read-only) */}
          {!isCreate && waiver?.customer_response_text && (
            <PaperFormSection
              label="Customer Response"
              hint="received from customer"
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "#fafafa",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 14 }}>
                  {waiver.customer_response_text}
                </Typography>
                {waiver.customer_response_doc_id && (
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1.5 }}>
                    Signed waiver document: #{waiver.customer_response_doc_id}
                  </Typography>
                )}
              </Paper>
            </PaperFormSection>
          )}

          <PaperFormFooter
            citation="AISC §1.7.0 §3.2 — customer drawing-approval waiver"
          />
        </PaperFormCard>

        {/* ACTION BAR */}
        <Box sx={{ maxWidth: "md", mx: "auto", px: { xs: 2, md: 3 }, pb: 6 }}>
          <Stack direction="row" justifyContent="flex-end" spacing={1} flexWrap="wrap">
            {/* LIFECYCLE ACTIONS — only visible post-create */}
            {!isCreate && next.includes("RECEIVED") && (
              <Button
                startIcon={<ReplyRounded />} variant="outlined"
                onClick={() => setOpenReceive(true)}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Record response
              </Button>
            )}
            {!isCreate && next.includes("ACTIVE") && (
              <Button
                startIcon={<CheckCircleOutlineRounded />} variant="contained" color="success"
                onClick={handleActivate} disabled={acting}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Activate
              </Button>
            )}
            {!isCreate && next.includes("REVOKED") && (
              <Button
                startIcon={<CancelRounded />} variant="outlined" color="error"
                onClick={() => setOpenRevoke(true)}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Revoke
              </Button>
            )}

            {canEdit && (
              <>
                <Box sx={{ flex: 1 }} />
                <Button
                  variant="text"
                  onClick={() => navigate("/customer-waivers")}
                  disabled={saving}
                  sx={{ color: "text.secondary" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<GavelOutlined />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    px: 3,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {saving ? "Saving…" : (isCreate ? "Create Request" : "Save changes")}
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Box>

      <Dialog open={openReceive} onClose={() => setOpenReceive(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record customer response</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FieldLabel hint="paste customer reply, email, or signed text">Response Text</FieldLabel>
            <TextField
              fullWidth multiline rows={4}
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              sx={{ mb: 2 }}
            />
            <FieldLabel hint="optional · upload signed waiver as a Document first">
              Signed Document ID
            </FieldLabel>
            <TextField
              fullWidth type="number"
              placeholder="e.g., 142"
              value={responseDocId}
              onChange={(e) => setResponseDocId(e.target.value)}
              helperText="Either response text OR doc ID required."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReceive(false)} disabled={acting}>Cancel</Button>
          <Button variant="contained" onClick={handleReceive} disabled={acting}>
            {acting ? "Saving…" : "Record response"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openRevoke} onClose={() => setOpenRevoke(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: "error.main" }}>Revoke waiver</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Revoking an ACTIVE waiver also clears <code>jobs.customer_drawing_approval_waived</code> on
            the linked Job. The audit_log entry persists indefinitely.
          </Alert>
          <FieldLabel required hint="captured in audit_log">Revoke Reason</FieldLabel>
          <TextField
            fullWidth multiline rows={3}
            placeholder="e.g., Customer reverted election; project scope expanded; pre-pilot test cleanup"
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRevoke(false)} disabled={acting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRevoke} disabled={acting}>
            {acting ? "Revoking…" : "Revoke waiver"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
